
/**
 * Teachers database service - main entry point
 * Provides a unified interface for teacher CRUD operations
 */

import { teachersFileStorageService } from './teachersFileStorageService';
import { Teacher as MainTeacher } from '@/types/teacher.types';

// Re-export all notification-related functions and types
export * from './notifications/index';
export * from './history-operations';

// Export types from the types file
export type { 
  TeacherNotification, 
  TeacherNotificationType, 
  TeacherDBSchema 
} from './types';

// Convert from file storage Teacher type to main Teacher type
const convertToMainTeacher = (fileTeacher: any): MainTeacher => {
  console.log('🔄 Converting file teacher to main teacher, profileImage:', fileTeacher.profileImage ? 'YES' : 'NO');
  console.log('📄 Converting teacher documents:', fileTeacher.documents?.length || 0, 'documents');
  
  return {
    id: fileTeacher.id,
    firstName: fileTeacher.firstName || '',
    lastName: fileTeacher.lastName || '',
    fatherName: fileTeacher.fatherName || '',
    gender: fileTeacher.gender || '',
    position: fileTeacher.position || '',
    idNumber: fileTeacher.idNumber || '',
    email: fileTeacher.email || '',
    phone: fileTeacher.phone || '',
    country: fileTeacher.country || 'Afghanistan',
    city: fileTeacher.city || '',
    address: fileTeacher.address || '',
    age: fileTeacher.age || '',
    dateOfBirth: fileTeacher.dateOfBirth || '',
    maritalStatus: fileTeacher.maritalStatus || '',
    joinDate: fileTeacher.hireDate || fileTeacher.joinDate || new Date().toISOString(),
    contractEndDate: fileTeacher.contractEndDate || '',
    salary: fileTeacher.salary?.toString() || '0',
    paymentStatus: 'pending',
    employmentType: fileTeacher.employmentType || 'full_time',
    status: fileTeacher.status || 'active',
    profileImage: fileTeacher.profileImage || '',
    bio: fileTeacher.bio || '',
    departments: (fileTeacher.departments || []).map((dept: string, index: number) => ({
      id: `dept_${index}`,
      name: dept,
      subject: '',
      classType: '',
      startTime: '',
      endTime: '',
      days: []
    })),
    subjects: fileTeacher.subjects || [],
    qualifications: fileTeacher.qualifications || [],
    documents: (fileTeacher.documents || []).map((doc: any, index: number) => {
      // If it's already a proper document object, return it
      if (typeof doc === 'object' && doc.id && doc.name) {
        return {
          id: doc.id,
          name: doc.name,
          size: doc.size || 0,
          type: doc.type || 'application/pdf',
          url: doc.url || '',
          documentType: doc.documentType || 'general',
          dateAdded: doc.dateAdded || new Date().toISOString(),
          status: doc.status || 'valid'
        };
      }
      // If it's just a string, create a legacy document object
      return {
        id: `doc_${index}`,
        name: typeof doc === 'string' ? doc : doc.name || `Document ${index + 1}`,
        size: 0,
        type: 'application/pdf',
        url: '',
        documentType: 'general' as const,
        dateAdded: new Date().toISOString(),
        status: 'legacy' as const
      };
    }),
    payments: fileTeacher.paymentHistory || [],
    history: fileTeacher.history || [],
    createdAt: fileTeacher.createdAt || new Date().toISOString(),
    updatedAt: fileTeacher.updatedAt || new Date().toISOString()
  };
};

const convertToFileTeacher = (mainTeacher: MainTeacher): any => {
  console.log('🔄 Converting main teacher to file teacher, profileImage:', mainTeacher.profileImage ? 'YES' : 'NO');
  console.log('📄 Converting main teacher documents:', mainTeacher.documents?.length || 0, 'documents');
  
  return {
    id: mainTeacher.id,
    firstName: mainTeacher.firstName,
    lastName: mainTeacher.lastName,
    fatherName: mainTeacher.fatherName,
    gender: mainTeacher.gender,
    position: mainTeacher.position,
    idNumber: mainTeacher.idNumber,
    email: mainTeacher.email,
    phone: mainTeacher.phone,
    country: mainTeacher.country,
    city: mainTeacher.city,
    address: mainTeacher.address,
    age: mainTeacher.age,
    dateOfBirth: mainTeacher.dateOfBirth,
    maritalStatus: mainTeacher.maritalStatus,
    departments: mainTeacher.departments?.map(d => d.name) || [],
    subjects: mainTeacher.subjects || [],
    employmentType: mainTeacher.employmentType as 'Full-time' | 'Part-time' | 'Contract' || 'Full-time',
    salary: parseFloat(mainTeacher.salary) || 0,
    hireDate: mainTeacher.joinDate,
    status: mainTeacher.status as 'Active' | 'Inactive' | 'On Leave' || 'Active',
    qualifications: mainTeacher.qualifications || [],
    documents: mainTeacher.documents || [], // Keep the full document objects
    paymentHistory: mainTeacher.payments || [],
    attendance: [],
    history: mainTeacher.history || [],
    profileImage: mainTeacher.profileImage || '',
    bio: mainTeacher.bio || '',
    createdAt: mainTeacher.createdAt,
    updatedAt: mainTeacher.updatedAt
  };
};

export const getTeachers = async (): Promise<MainTeacher[]> => {
  try {
    const fileTeachers = await teachersFileStorageService.getAllTeachers();
    return fileTeachers.map(convertToMainTeacher);
  } catch (error) {
    console.error('Error getting teachers:', error);
    return [];
  }
};

export const getTeacherById = async (id: string): Promise<MainTeacher | null> => {
  try {
    const fileTeacher = await teachersFileStorageService.getTeacherById(id);
    return fileTeacher ? convertToMainTeacher(fileTeacher) : null;
  } catch (error) {
    console.error('Error getting teacher by ID:', error);
    return null;
  }
};

export const saveTeacher = async (teacherData: any): Promise<MainTeacher> => {
  try {
    console.log('💾 Saving teacher with profileImage:', teacherData.profileImage ? 'YES' : 'NO');
    
    const fileTeacher = convertToFileTeacher(teacherData);
    
    // Check if this is an existing teacher (has ID and exists in database)
    if (teacherData.id) {
      const existingTeacher = await teachersFileStorageService.getTeacherById(teacherData.id);
      
      if (existingTeacher) {
        // Update existing teacher
        console.log('📝 Updating existing teacher:', teacherData.id);
        const updatedTeacher = await teachersFileStorageService.updateTeacher(teacherData.id, fileTeacher);
        if (!updatedTeacher) {
          throw new Error('Failed to update teacher');
        }
        return convertToMainTeacher(updatedTeacher);
      }
    }
    
    // Add new teacher
    console.log('➕ Adding new teacher');
    const newTeacher = await teachersFileStorageService.addTeacher(fileTeacher);
    return convertToMainTeacher(newTeacher);
  } catch (error) {
    console.error('Error saving teacher:', error);
    throw error;
  }
};

export const deleteTeacher = async (id: string): Promise<void> => {
  try {
    await teachersFileStorageService.deleteTeacher(id);
  } catch (error) {
    console.error('Error deleting teacher:', error);
    throw error;
  }
};

export const searchTeachers = async (query: string): Promise<MainTeacher[]> => {
  try {
    const fileTeachers = await teachersFileStorageService.searchTeachers(query);
    return fileTeachers.map(convertToMainTeacher);
  } catch (error) {
    console.error('Error searching teachers:', error);
    return [];
  }
};

// Re-export Teacher type for convenience
export type { MainTeacher as Teacher };
